/**
 * Module for dynamically resizing textareas, based on their contents' height.
 */

class AutoExpandTextareas {

  /**
   * @param {object} [options] -
   * @param {string} [options.selector] - Selector for textareas to attach to
   * @param {array} [options.resizeEvents] - List of events that should trigger an immediate resize
   * @param {array} [options.resizeDelayedEvents] - List of events that should trigger a resize
   */
  constructor(options) {
    const defaults = {
      selector: '[data-auto-expand]',
      resizeEvents: ['change'],
      resizeDelayedEvents: ['cut', 'paste', 'drop', 'keydown']
    };
    this.settings = Object.assign({}, defaults, options);
  }

  /**
   * Creates bound listener functions
   */
  init() {
    this.textAreaElements = Array.from(document.querySelectorAll(this.settings.selector));
    this.bindEvents();
  }

  /**
   * Binds event handlers to events as per settings given in constructor
   */
  bindEvents() {
    this.resizeHandler = this.resize.bind(this);
    this.resizeDelayedHandler = this.resizeDelayed.bind(this);
    this.settings.resizeEvents.forEach(this.bindResizeEvent, this);
    this.settings.resizeDelayedEvents.forEach(this.bindResizeDelayedEvent, this);
  }

  /**
   * Attaches event listeners for a given event
   * @param {string} eventName - The target event's name
   */
  bindResizeEvent(eventName) {
    this.textAreaElements.forEach(el => {
      el.addEventListener(eventName, this.resizeHandler);
    });
  }

  /**
   * Attaches event listeners for a given event
   * @param {string} eventName - The target event's name
   */
  bindResizeDelayedEvent(eventName) {
    this.textAreaElements.forEach(el => {
      el.addEventListener(eventName, this.resizeDelayedHandler);
    });
  }

  /**
   * Determines the vertical height an element needs, and resizes it to fit.
   *
   * @param {object} event - The event object passed to the listener
   *
   * @returns {function} - Event listener function with the target element in closure scope
   */
  resize(event) {
    let element = event.target;
    return function() {
      if(!element.style.minHeight) {
        element.style.minHeight = element.getBoundingClientRect().height + 'px';
      }
      element.style.height = 'auto';
      element.style.height = element.scrollHeight + 'px';
    }
  }

  /**
   * Same as `resize` function, but delayed to ensure DOM has been updated first.
   *
   * @param {object} event - The event object passed to the listener
   */
  resizeDelayed(event) {
    setTimeout(this.resize(event), 0);
  }
}

module.exports = AutoExpandTextareas;
